<script setup lang="ts">
import { CourseType } from "~/enums/course.enums";

const { closeFeedbackForm } = useCourseState();
const { submitFeedback } = useCourse();
const { t } = useI18n();

const props = defineProps<{
  courseId: number;
  courseTitle: string;
  courseType: string;
  skippable: boolean;
  progress: number;
}>();

const modelValue = defineModel<boolean>();
const hoverIndex = ref(0);
const rating = ref(0);
const feedback = ref('');
const ratingError = ref<string | null>(null);

const hoverOver = (index: number) => {
  hoverIndex.value = index;
};

const hoverOut = () => {
  hoverIndex.value = 0;
};

const setRating = (index: number) => {
  rating.value = index;
};

const isFormValid = computed(() => {
  return !!rating.value;
});

const submit = async () => {
  const response = await submitFeedback(props.courseId, rating.value, props.progress, feedback.value);

  if (response.error && response.error.value) {
    if (response.error.value.statusCode === 422) {
      if (response.error.value.data.errors.rating) {
        ratingError.value = response.error.value.data.errors.rating[0];
      }
    }
  } else {
    closeFeedbackForm('submitted');
  }
};

const remindMeLater = () => {
  if (!props.skippable) {
    ratingError.value = 'Please rate the course before you continue.';
    return;
  }

  closeFeedbackForm('remindMeLater');
};
</script>

<template>
  <Dialog
    v-model:visible="modelValue"
    :draggable="false"
    modal
    :header="t('course.feedback.header', {title: courseTitle})"
    class="sm:w-[500px]"
    :closable="false"
    @hide="remindMeLater()"
  >
    <template #default>
      <MField
        :label="t('course.feedback.ratingLabel')"
        :error="ratingError || undefined"
      >
        <div
          class="rating"
          @mouseout="hoverOut"
        >
          <MIcon
            v-for="index in 5"
            :key="index"
            icon="star"
            :class="{'active': index <= rating && hoverIndex === 0, 'highlight': index <= hoverIndex}"
            @mouseover="hoverOver(index)"
            @click="setRating(index)"
          />
        </div>
      </MField>

      <MField
        class="!mb-0"
        :label="t(`course.feedback.explanationLabel.${props.courseType === CourseType.Course ? 'course' : 'workshop'}`)"
      >
        <Textarea
          v-model="feedback"
          rows="5"
          class="resize-y max-h-[400px] min-h-[60px]"
        />
      </MField>
    </template>

    <template #footer>
      <div class="flex items-center justify-between gap-4 w-full">
        <Button
          v-if="skippable"
          severity="secondary"
          class="no-rating text-neutral-80 font-bold"
          text
          :label="t(`course.feedback.${progress >= 0.5 ? 'askAtEnd' : 'askLater'}`)"
          href="#no-rating"
          @click.prevent="remindMeLater()"
        />

        <Button
          severity="info"
          class="rounded-full text-nowrap bg-blue-30 border-blue-30 text-neutral-90 font-bold"
          :label="t('course.feedback.submit')"
          :disabled="!isFormValid"
          @click="submit()"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.m-field {
  margin-bottom: 2rem;

  .label {
    display: block;
    text-align: center;
  }
}

.feedback-text {
  width: 100%;
  height: 8rem;
  padding: 0.5rem;
  resize: none;
}

.rating {
  padding: 0;
  display: flex;
  justify-content: center;

  .m-icon {
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--palette-neutral-30);
    transition: color 0.1s ease-in-out;
    margin-right: 0.5rem;

    &.active,
    &.highlight {
      color: var(--palette-neutral-70);
    }
  }
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0 0 0;

  .no-rating {
    font-size: 0.875rem;
    text-decoration: underline;
  }

  .submit {
    margin-left: auto;
  }
}
</style>